import { create } from 'zustand';
import type { SavedSearch } from '@/types/auth';
import { getSavedSearches, createSavedSearch, deleteSavedSearch, updateSavedSearch } from '@/lib/api';

interface SavedSearchState {
  searches: SavedSearch[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchSearches: () => Promise<void>;
  saveSearch: (name: string, filters: Record<string, any>, notify?: boolean) => Promise<void>;
  removeSearch: (id: string) => Promise<void>;
  patchSearch: (id: string, patch: { name?: string; notify?: boolean }) => Promise<void>;
  clearSearches: () => void;
}

export const useSavedSearchStore = create<SavedSearchState>((set, get) => ({
  searches: [],
  isLoading: false,
  error: null,

  /**
   * Load all saved searches for the current user
   */
  fetchSearches: async () => {
    set({ isLoading: true, error: null });

    try {
      const searches = await getSavedSearches();
      set({ searches, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load saved searches';
      set({ error: message, isLoading: false });
    }
  },

  /**
   * Save a new search with the given filters
   */
  saveSearch: async (name: string, filters: Record<string, any>, notify?: boolean) => {
    set({ error: null });

    try {
      const search = await createSavedSearch(name, filters, notify);
      set((state) => ({ searches: [search, ...state.searches] }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save search';
      set({ error: message });
      throw err;
    }
  },

  /**
   * Delete a saved search by ID
   */
  removeSearch: async (id: string) => {
    const { searches } = get();

    // Optimistic update
    set({ searches: searches.filter((s) => s.id !== id) });

    try {
      await deleteSavedSearch(id);
    } catch (err) {
      // Revert on error
      set({ searches });
      const message = err instanceof Error ? err.message : 'Failed to delete saved search';
      set({ error: message });
      throw err;
    }
  },

  /**
   * Rename or toggle alerts on a saved search.
   */
  patchSearch: async (id, patch) => {
    const updated = await updateSavedSearch(id, patch);
    set({ searches: get().searches.map((s) => (s.id === id ? updated : s)) });
  },

  /**
   * Clear saved searches (call on logout)
   */
  clearSearches: () => {
    set({ searches: [], error: null });
  },
}));
